import { forwardRef } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { cn } from './cn';

export type BadgeColor = 'primary' | 'danger' | 'success';

type NativeSpanProps = Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  // These collide with motion's own (PanInfo-based) gesture/animation handlers.
  'onDrag' | 'onDragStart' | 'onDragEnd' | 'onAnimationStart' | 'onAnimationEnd'
>;

export interface BadgeProps extends NativeSpanProps {
  /** Numeric count to display. Hidden when 0 (unless in dot mode). */
  count?: number;
  /** Cap value — counts above this render as `${max}+`. Default 99. */
  max?: number;
  /** Render a small dot instead of a number. */
  dot?: boolean;
  /** Force-hide the badge regardless of count. Default true. */
  show?: boolean;
  /** Badge fill color. Default 'primary'. */
  color?: BadgeColor;
  /** When provided, the badge is positioned at the top-right of this content. */
  children?: React.ReactNode;
}

const colors: Record<BadgeColor, string> = {
  primary: 'bg-primary',
  danger: 'bg-danger',
  success: 'bg-success',
};

const POP = { type: 'spring', stiffness: 520, damping: 24 } as const;

/**
 * Notification count badge — replaces ad-hoc MUI <Badge /> usage.
 * Standalone (no children) renders just the badge; with children it overlays
 * the top-right corner. Use `dot` for a presence indicator, `count` + `max`
 * for a number that caps at `${max}+`.
 */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      count = 0,
      max = 99,
      dot = false,
      show = true,
      color = 'primary',
      children,
      className,
      ...props
    },
    ref,
  ) => {
    const visible = show && (dot || count > 0);
    const display = count > max ? `${max}+` : String(count);

    const badge = (
      <AnimatePresence>
        {visible && (
          <motion.span
            ref={ref}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            transition={POP}
            className={cn(
              'pointer-events-none select-none text-white',
              children && 'absolute -top-1 -right-1 z-[1]',
              dot
                ? 'block size-2.5 rounded-full ring-2 ring-surface'
                : 'grid h-5 min-w-5 place-items-center rounded-full px-1.5 text-[11px] font-semibold leading-none',
              colors[color],
              className,
            )}
            {...props}
          >
            {!dot && display}
          </motion.span>
        )}
      </AnimatePresence>
    );

    if (!children) return badge;

    return (
      <span className="relative inline-flex">
        {children}
        {badge}
      </span>
    );
  },
);

Badge.displayName = 'Badge';
